<template>
  <div class="v-type-select" v-click-outside="hideDropdown">
    <div class="v-type-select__field" @click="toggleDropdown">
      <input
        v-if="showDropdown"
        ref="typeInput"
        v-focus
        v-model="searchText"
        type="text"
        :placeholder="searchPlaceholder"
        class="v-type-select__field-input"
        @click.stop
        @input="onSearchInput"
        @keydown.enter="onEnter"
        @keydown.esc="onEscape"
      />
      <div
        v-else
        class="v-type-select__title"
        :class="{ 'v-type-select__title--hidden': !searchText }"
      >
        {{ searchText || label }}
      </div>
      <label
        class="v-type-select__label"
        :class="{ 
          'v-type-select__label--active': searchText || showDropdown, 
          'v-type-select__label--invalid': invalid 
        }"
      >
        {{ label }}
      </label>
      <div class="v-type-select__bar"></div>
      <div class="v-type-select__icon">
        <v-chevron :value="showDropdown" />
      </div>
    </div>
    <div v-if="hint" class="v-type-select__hint">{{ hint }}</div>
    
    <transition name="fade">
      <div
        v-if="showDropdown"
        class="v-type-select__items"
        :class="{ 
          'v-type-select__items--fix-height': hasResults && (filteredCustomTypes.length + filteredDefaultTypes.length) > 4 
        }"
        @click="onDropdownClick"
      >
        <vue-scroll>
          <div
            v-if="canShowCreateButton"
            class="item item--create"
            @click.stop="enableCreate"
          >
            <v-icon
              font-size="1"
              icon="plus"
              position-left
            />
            <span>{{ createNewText }}</span>
          </div>
          
          <div v-if="canShowCreateButton && (filteredCustomTypes.length || filteredDefaultTypes.length)" class="divider"></div>
          
          <template v-if="filteredCustomTypes.length">
            <div
              v-for="type in filteredCustomTypes"
              :key="'custom-' + type.id"
              class="item item--custom"
              @click.stop="selectType(type, false)"
            >
              <span class="item__text">{{ type.name }}</span>
            </div>
          </template>
          
          <div v-if="filteredCustomTypes.length && filteredDefaultTypes.length" class="divider"></div>
          
          <template v-if="filteredDefaultTypes.length">
            <div v-if="filteredCustomTypes.length" class="item__label">{{ recommendedText }}</div>
            <div
              v-for="type in filteredDefaultTypes"
              :key="'default-' + type.id"
              class="item"
              @click.stop="selectType(type, true)"
            >
              <span class="item__text">{{ type.name }}</span>
            </div>
          </template>
          
          <div v-if="!hasResults" class="empty">
            {{ emptyText }}
          </div>
        </vue-scroll>
      </div>
    </transition>
  </div>
</template>

<script>
import VChevron from '@/components/ui/v-chevron/VChevron'

export default {
  name: 'VTypeSelect',

  components: {
    VChevron
  },

  props: {
    value: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      required: true
    },
    hint: {
      type: String,
      default: null
    },
    invalid: {
      type: Boolean,
      default: false
    },
    customTypes: {
      type: Array,
      default: () => []
    },
    defaultTypes: {
      type: Array,
      default: () => []
    },
    searchPlaceholder: {
      type: String,
      default: function () {
        return this.$t('base.search')
      }
    },
    createNewText: {
      type: String,
      default: function () {
        return this.$t('base.add_as_new_type')
      }
    },
    recommendedText: {
      type: String,
      default: function () {
        return this.$t('base.recommended')
      }
    },
    emptyText: {
      type: String,
      default: function () {
        return this.$t('base.no_options')
      }
    },
    autoCloseOnSelect: {
      type: Boolean,
      default: true
    },
    // НОВИЙ PROP для CreateTypeModal та CreateIngredientTypeModal
    closeOnReselect: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    searchText: '',
    showDropdown: false,
    selectedType: null,
    createEnabled: false,
    originalValue: '',
    selectedTypeName: '',
    userStartedEditing: false
  }),

  computed: {
    filteredCustomTypes () {
      if (!this.searchText) return this.customTypes
      const search = this.searchText.toLowerCase()
      return this.customTypes.filter(type =>
        type.name.toLowerCase().includes(search)
      )
    },

    filteredDefaultTypes () {
      if (!this.searchText) return this.defaultTypes

      const search = this.searchText.toLowerCase()
      return this.defaultTypes.filter(type =>
        type.name.toLowerCase().includes(search)
      )
    },

    exactMatch () {
      if (!this.searchText) return false
      const allTypes = [...this.customTypes, ...this.defaultTypes]
      return allTypes.some(type =>
        type.name.toLowerCase() === this.searchText.toLowerCase()
      )
    },

    hasResults () {
      return this.filteredCustomTypes.length > 0 ||
             this.filteredDefaultTypes.length > 0 ||
             this.canShowCreateButton
    },

    canShowCreateButton () {
      return this.searchText && !this.exactMatch
    }
  },

  watch: {
    value: {
      immediate: true,
      handler (newVal) {
        this.searchText = newVal
        this.originalValue = newVal
      }
    }
  },

  methods: {
    toggleDropdown () {
      if (!this.showDropdown) {
        this.showDropdown = true
        this.searchText = ''
        this.userStartedEditing = false

        this.$nextTick(() => {
          const input = this.$el.querySelector('.v-type-select__field-input')
          if (input) {
            input.focus()
          }
        })
      } else {
        this.showDropdown = false
      }
    },

    hideDropdown () {
      if (this.showDropdown) {
        this.showDropdown = false
        this.userStartedEditing = false

        if (!this.searchText) {
          this.searchText = this.originalValue
          this.$emit('input', this.originalValue)
        } else if (this.searchText && !this.exactMatch && !this.selectedType) {
          this.createEnabled = true
          this.$emit('input', this.searchText)
          this.$emit('create-enabled', true)
        }
      }
    },

    onEnter () {
      // Enter закриває dropdown
      if (this.showDropdown) {
        this.showDropdown = false
      }
    },

    onEscape () {
      // Escape закриває dropdown
      if (this.showDropdown) {
        this.showDropdown = false
      }
    },

    onDropdownClick (event) {
      // Клік на порожню область dropdown закриває його
      if (event.target.classList.contains('v-type-select__items') ||
          event.target.closest('.vue-scroll')) {
        if (!event.target.closest('.item') &&
            !event.target.closest('.divider') &&
            !event.target.closest('.empty')) {
          this.showDropdown = false
        }
      }
    },

    onSearchInput () {
      if (this.selectedTypeName) {
        this.userStartedEditing = true
      }

      this.selectedType = null
      this.createEnabled = false
      this.$emit('input', this.searchText)
      this.$emit('create-enabled', false)
    },

    selectType (type, isDefault = false) {
      this.searchText = type.name
      this.originalValue = type.name
      this.selectedType = type
      this.selectedTypeName = type.name
      this.userStartedEditing = false

      this.createEnabled = true

      this.$emit('input', type.name)
      this.$emit('select', { type, isDefault })
      this.$emit('create-enabled', true)

      // Для модалок з окремим полем "Назва" - завжди закриваємо dropdown
      if (this.autoCloseOnSelect) {
        this.showDropdown = false
        return
      }

      // Для CreateTypeModal та CreateIngredientTypeModal з closeOnReselect:
      // Закриваємо dropdown одразу при виборі зі списку (1 клік = підтвердження)
      if (this.closeOnReselect) {
        this.showDropdown = false
        return
      }

      // Для інших випадків - залишаємо відкритим з можливістю редагувати
      this.$nextTick(() => {
        const input = this.$el.querySelector('.v-type-select__field-input')
        if (input) {
          input.focus()
          input.setSelectionRange(input.value.length, input.value.length)

          const onBlur = () => {
            if (!this.userStartedEditing && this.searchText === this.selectedTypeName) {
              setTimeout(() => {
                this.showDropdown = false
              }, 150)
            }
            input.removeEventListener('blur', onBlur)
          }

          input.addEventListener('blur', onBlur)
        }
      })
    },

    enableCreate () {
      this.createEnabled = true
      this.originalValue = this.searchText
      this.showDropdown = false
      this.selectedType = null
      this.selectedTypeName = ''
      this.userStartedEditing = false

      this.$emit('input', this.searchText)
      this.$emit('create-enabled', true)

      this.$emit('create-new', this.searchText)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/sass/abstracts/_variables.scss';

.v-type-select {
  position: relative;

  &__field {
    position: relative;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    font-size: 1.5rem;
    width: 100%;
    height: 4rem;
    padding: 1rem 0;
    border-bottom: .1rem solid $color-gray-light-2;

    &-input {
      width: 100%;
      border: none;
      font-size: 1.4rem;

      &:focus {
        outline: none;
      }

      &:focus ~ label,
      &:valid ~ label {
        color: $color-gray;
        top: -.6rem;
        font-size: 1.3rem;
      }

      &:focus ~ .v-type-select__bar:before,
      &:focus ~ .v-type-select__bar:after {
        width: 50%;
      }
    }
  }

  &__title {
    margin-right: 1.8rem;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    max-width: 24rem;
    font-size: 1.5rem;
    user-select: none;

    &--hidden {
      opacity: 0;
    }
  }

  &__label {
    color: $color-black;
    font-size: 1.5rem;
    position: absolute;
    pointer-events: none;
    top: 1rem;
    transition: 0.2s ease all;

    &--active {
      color: $color-gray;
      font-size: 1.3rem;
      top: -.8rem;
    }
    
    &--invalid {
      color: $color-red !important;
      font-size: 1.3rem;
      top: -.8rem;
    }
  }

  &__bar {
    &:before,
    &:after {
      content: "";
      height: .2rem;
      width: 0;
      bottom: 0;
      position: absolute;
      background: $color-primary;
      transition: 0.2s ease all;
    }

    &:before {
      left: 50%;
    }

    &:after {
      right: 50%;
    }
  }

  &__icon {
    transition: transform .3s ease;
  }

  &__items {
    position: absolute;
    z-index: 1000;
    width: 100%;
    min-height: 4rem;
    background: $color-white;
    padding: 0.5rem 0;
    border-radius: 1rem;
    box-shadow: 2px 2px 5px 0px rgba(0, 0, 0, .2);

    &--fix-height {
      height: 21rem;
    }
  }

  &__hint {
    color: $color-red;
    font-size: 1.2rem;
    font-weight: 300;
    padding-top: .3rem;
  }
}

.item {
  cursor: pointer;
  user-select: none;
  position: relative;
  padding: 1rem;
  width: 100%;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  border-radius: 10px;
  width: calc(100% - 10px);
  margin: 0 auto;
  transition: 0.3s all;

  &:hover {
    background: #DDF2E3;
  }

  &--create {
    color: $color-primary;
    font-weight: 500;
    transition: 0.3s all;
    svg {
        color: #1DA643;
      }
    &:hover {
      background: #DDF2E3;
      color: #000;
      svg {
        color: #1DA643;
      }
    }
  }

  &--custom {
    .item__text::before {
      content: '';
      display: inline-block;
      width: 20px;
      height: 20px;
      min-width: 20px;
      margin-right: 8px;
      vertical-align: middle;
      background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4.81445 9.34955C4.81445 8.81912 5.02517 8.31041 5.40024 7.93534L9.29253 4.04304C9.68306 3.65252 10.3162 3.65252 10.7067 4.04304L14.599 7.93534C14.9741 8.31041 15.1848 8.81912 15.1848 9.34955V14.6693C15.1848 15.7738 14.2894 16.6693 13.1848 16.6693H9.99964H6.81445C5.70988 16.6693 4.81445 15.7738 4.81445 14.6693V9.34955Z' stroke='%231DA643' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
    }
  }

  &__text {
    display: flex;
    align-items: center;
  }

  &__label {
    padding: .8rem 1rem;
    font-size: 1.2rem;
    font-weight: 600;
    color: $color-gray;
    text-transform: uppercase;
  }
}

.divider {
  height: .1rem;
  background: $color-gray-light-2;
  margin: .4rem 0;
}

.empty {
  font-size: 1.5rem;
  padding: .5rem 1rem;
  color: $color-gray;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .2s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>